import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { CallbackDataParams } from 'echarts/types/dist/shared';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

interface DailyStat {
  date: string;
  count: number;
}

const getChartOptions = (
  getThemeColor: (name: string) => string,
  data: DailyStat[]
) => {
  // Fill in missing days with 0 count for last 30 days
  const last30Days: string[] = [];
  for (let i = 29; i >= 0; i--) {
    last30Days.push(dayjs().subtract(i, 'day').format('YYYY-MM-DD'));
  }

  const dataMap = new Map(
    data.map(d => [dayjs(d.date).format('YYYY-MM-DD'), d.count])
  );

  const dates = last30Days;
  const counts = dates.map(d => dataMap.get(d) || 0);

  return {
    color: [getThemeColor('primary')],
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('light-text-emphasis'), fontSize: 12 },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: CallbackDataParams[]) => {
        const p = params[0];
        return `<strong>${dayjs(p.name).format('DD MMM YYYY')}</strong><br/>Claims: ${p.value}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLine: {
        lineStyle: { color: getThemeColor('border-color') }
      },
      axisTick: { show: false },
      axisLabel: {
        formatter: (value: string) => dayjs(value).format('DD MMM'),
        interval: 4,
        color: getThemeColor('secondary-color'),
        fontSize: 11,
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: { color: getThemeColor('border-color'), type: 'dashed' }
      },
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontSize: 11
      }
    },
    series: [
      {
        name: 'Claims',
        type: 'bar',
        data: counts,
        barMaxWidth: 20,
        itemStyle: {
          borderRadius: [4, 4, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: getThemeColor('primary') },
            { offset: 1, color: getThemeColor('info') }
          ])
        },
        emphasis: {
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: getThemeColor('info') },
              { offset: 1, color: getThemeColor('primary') }
            ])
          }
        }
      }
    ],
    grid: {
      left: 40,
      right: 16,
      bottom: 60,
      top: 16
    }
  };
};

interface ClaimRecordsDailyChartProps {
  data: DailyStat[];
}

const ClaimRecordsDailyChart = ({ data }: ClaimRecordsDailyChartProps) => {
  const { getThemeColor } = useAppContext();

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getChartOptions(getThemeColor, data)}
      style={{ height: '320px', width: '100%' }}
    />
  );
};

export default ClaimRecordsDailyChart;
